import audioCacheService from '../../service/audio-cache-service';
import ClipService from '../../service/clip-service';

export const makeAudioActions = (set, get) => {
    const cacheProjectAudioImpl = async (projectId) => {
        if (get('audioCacheLoading')) return;
        set('audioCacheLoading', true);
        set('audioCacheError', null);

        try {
            const cueSheet = get('cueSheet');
            const existingCachedUrls = get('cachedAudioUrls');
            const cachedUrls = { ...existingCachedUrls };

            for (const item of cueSheet) {
                if (!item.clip) continue;
                const clipId = item.clip.id;

                if (cachedUrls[clipId]) continue;

                let blobUrl = await audioCacheService.getCachedAudio(projectId, clipId);
                if (!blobUrl) {
                    const blob = await ClipService.downloadClipAsync(clipId);
                    if (blob) {
                        blobUrl = await audioCacheService.cacheAudio(projectId, clipId, blob);
                    }
                }
                if (blobUrl) {
                    cachedUrls[clipId] = blobUrl;
                }
            }

            set('cachedAudioUrls', cachedUrls);
            set('audioCacheLoading', false);
        } catch (error) {
            set('audioCacheError', error);
            set('audioCacheLoading', false);
            console.error('Audio caching failed:', error);
        }
    };

    const cleanupAudioCacheImpl = async (projectId, clipId = null) => {
        const cachedUrls = get('cachedAudioUrls');

        if (clipId !== null) {
            const blobUrl = cachedUrls[clipId];
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
                await audioCacheService.deleteCachedAudio(projectId, clipId);
                const updated = { ...cachedUrls };
                delete updated[clipId];
                set('cachedAudioUrls', updated);
            }
        } else {
            Object.values(cachedUrls).forEach(url => URL.revokeObjectURL(url));
            set('cachedAudioUrls', {});
        }
    };

    // Bumped by every cache / cleanup, so a download that finishes after the
    // user moved to another session does not put its URLs back.
    let recAudioGeneration = 0;

    /**
     * Caches the clips of a rec session and publishes each blob URL as soon as
     * it is ready, so every track appears on the timeline as it arrives instead
     * of all of them waiting for the slowest one.
     */
    const cacheRecSessionAudioImpl = async (projectId, session) => {
        if (!session) return;
        const generation = ++recAudioGeneration;

        // One download per clip, even when several tracks share it.
        const clips = new Map();
        session.coreLayer?.tracks?.forEach(t => { if (t.clip) clips.set(t.clip.id, t.clip); });
        session.auxLayers?.forEach(layer => layer.tracks?.forEach(t => { if (t.clip) clips.set(t.clip.id, t.clip); }));

        await Promise.all([...clips.values()].map(async (clip) => {
            if (get('cachedRecAudioUrls')[clip.id]) return;

            try {
                let blobUrl = await audioCacheService.getCachedAudio(projectId, clip.id);
                if (!blobUrl) {
                    const blob = await ClipService.downloadClipAsync(clip.id);
                    if (blob) blobUrl = await audioCacheService.cacheAudio(projectId, clip.id, blob);
                }
                if (!blobUrl) return;

                if (generation !== recAudioGeneration) {
                    URL.revokeObjectURL(blobUrl);
                    return;
                }

                set('cachedRecAudioUrls', { ...get('cachedRecAudioUrls'), [clip.id]: blobUrl });
            } catch (error) {
                console.error('Rec session audio cache error:', clip.id, error);
            }
        }));
    };

    const cleanupRecSessionAudioImpl = () => {
        recAudioGeneration++;
        const cachedUrls = get('cachedRecAudioUrls');
        Object.values(cachedUrls).forEach(url => URL.revokeObjectURL(url));
        set('cachedRecAudioUrls', {});
    };

    return {
        cacheProjectAudio: cacheProjectAudioImpl,
        cleanupAudioCache: cleanupAudioCacheImpl,
        cacheRecSessionAudio: cacheRecSessionAudioImpl,
        cleanupRecSessionAudio: cleanupRecSessionAudioImpl,
        cacheRecSessionAudioImpl,
        cleanupRecSessionAudioImpl,
    };
};

export const actionsCaching = ({ set, get }) => ({
    cacheSelectedVideo: async () => {},
    cleanupVideoCache: async () => {},
});
